import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * NetlifyBypassLogin - A component that automatically redirects to dashboard
 * for Netlify deployments without requiring actual authentication.
 * 
 * This component is used exclusively for demo/Netlify deployments to allow
 * users to access the dashboard without needing to enter credentials.
 */
const NetlifyBypassLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // On component mount, perform auto-login and redirect
  useEffect(() => {
    const autoLogin = async () => {
      try {
        console.log('NetlifyBypassLogin: Setting up demo session and redirecting to dashboard');
        
        // Create a mock successful login for Netlify deployments
        localStorage.setItem('authToken', 'mock-auth-token-for-netlify');
        localStorage.setItem('refreshToken', 'mock-refresh-token-for-netlify');
        localStorage.setItem('user', JSON.stringify({
          id: '3',
          firstName: 'Demo',
          lastName: 'User',
          email: 'dev@roiexteriors.com',
          role: 'admin'
        }));
        
        // Call the login function with demo credentials to update the auth context state
        // This is a fallback in case the automatic session in AuthContext doesn't work
        await login('dev@roiexteriors.com', 'password123');
        
        // Short timeout to ensure context updates before redirect
        setTimeout(() => {
          console.log('NetlifyBypassLogin: Redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        }, 100);
      } catch (error) {
        console.error('Auto-login failed:', error);
        // Redirect to dashboard anyway since we're in Netlify mode
        console.log('NetlifyBypassLogin: Login failed but redirecting to dashboard anyway');
        navigate('/dashboard', { replace: true });
      }
    };
    
    // Immediately invoke autoLogin
    autoLogin();
    
    // Also set a fallback redirect
    const redirectTimeout = setTimeout(() => {
      console.log('NetlifyBypassLogin: Fallback redirect triggered');
      navigate('/dashboard', { replace: true });
    }, 2000);
    
    return () => clearTimeout(redirectTimeout);
  }, [navigate, login]);
  
  // Display a loading spinner while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-xl text-gray-600">Loading the dashboard...</p>
        <p className="text-sm text-gray-500 mt-2">You will be redirected automatically.</p>
      </div>
    </div>
  );
};

export default NetlifyBypassLogin;